import { orderStages, mapOrderTypeToTitle } from "@carpaddy-fixtures/constants"
import { has, update } from "ramda"
import { encrypt } from "@app/helpers"

export const paidForBackUpDocs = (order: any) => {
  return order.documents.some(
    (doc: any) => doc.key === "backup" || doc.key === "processing",
  )
}

export function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  }
}

export const saltUrl = (url: string, duration: number = 15) => {
  const root = `${process.env.REACT_APP_BASE_URL}/uploadx/`
  const urlPath = url.replace(root, "")
  const param = `${urlPath}+${new Date().toISOString()}+${
    process.env.REACT_APP_CP_API_KEY
  }+${duration}`
  const encryptedPath = encrypt(param, { strict: true })
  return `${root}${encodeURIComponent(encryptedPath)}`
}

export const editOrderDocsFn = (order: any, list: any) =>
  order.documents.reduce(
    (doc: any, curr: any) => {
      if (has(curr.key)(list) && list[curr.key] === true) {
        doc.price += curr?.price ? curr?.price * curr.qty : 0
        doc.cost += curr?.cost ? curr?.cost * curr.qty : 0
      } else {
        doc.documents.push(curr)
      }
      return doc
    },
    { documents: [], price: 0, cost: 0 },
  )

const getDeliveryAmount = (order: any) =>
  order.fees?.find((f: any) => f.name === "delivery")?.amount ?? 0

/** Switch order to online delivery: zero fee, mode=online, deliveryInfo={email only} */
const toOnlineDelivery = (order: any) => ({
  deliveryMode: "online",
  deliveryInfo: {
    email: order?.deliveryInfo?.email || order?.email || "",
  },
})

export const getTotalRefundForEditFn = (order: any, list: any) => {
  const updated = editOrderDocsFn(order, list)
  const deliveryRefund = list?.delivery === true ? getDeliveryAmount(order) : 0
  return updated.price + deliveryRefund
}

export const returnUpdatedOrderForEditFn = (order: any, list: any) => {
  const updated = editOrderDocsFn(order, list)
  const updatedFees = {
    ...order.fees[0],
    amount: order.fees[0].amount - updated.price,
    cost: order.fees[0].cost - updated.cost,
  }

  let fees = update(0, updatedFees, order.fees)

  if (list?.delivery === true) {
    fees = fees.map((fee: any) =>
      fee.name === "delivery" ? { ...fee, amount: 0 } : fee,
    )
  }

  const updatedOrder = {
    ...order,
    documents: updated.documents,
    fees,
    ...(list?.delivery === true && toOnlineDelivery(order)),
  }

  return updatedOrder
}

export const classifyOrders = (datas: any) => {
  const initialValues = {
    [orderStages.APPROVAL]: [],
    [orderStages.RECEIVED]: [],
    [orderStages.HOLD]: [],
    [orderStages.PROCESS]: [],
    [orderStages.WAITING]: [],
    [orderStages.DELIVER]: [],
    [orderStages.COMPLETE]: [],
  }

  return datas.reduce((sorted: any, data: any) => {
    sorted[data.status[0].type]?.push(data)
    return sorted
  }, initialValues)
}

// merges orders and cart items awaiting biometrics
export const mergeOrderObjects = (orderArray: any[], cartArray: any[]) => {
  const result = [...orderArray, ...cartArray]
  const classified = Object.entries(classifyOrders(result))
  return classified
}

export const generateWhatsappUrl = (text: string, phone: string) =>
  `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(
    text,
  )}&source=&data=`

export const generateEmailUpdate = (
  message: string,
  email: string,
  title: string,
) => ({
  sender_name: "CarPaddy.com",
  sender_email: "support@carpaddy.com",
  to: [email],
  bcc: ["support@carpaddy.com"],
  subject: title,
  htmlMessage: `<br> ${message} <br> -CarPaddy team`,
  // imageUrl:  "",
})

export const useSendUpdates = (order: any) => {
  const orderId = order?.orderid || order?.cartid
  const otp = orderId.slice(orderId.length - 4).toUpperCase()

  const deliveryText = (name: string, phone: string) =>
    `Your order has been sent: Expect a call from ${name} ${phone} between 9am-7pm today. The OTP for the delivery is - ${otp}. Thank you!`

  const deliveryCourierText = (name: string, phone: string) =>
    `Your order has been sent: Expect a call from our delivery partner (${name} ${phone}) between 9am-7pm today. The OTP for the delivery is - ${otp}. Thank you!`

  const deliveryOnlineText = () =>
    `Your order has been sent online as your delivery method. Thank you!`

  const deliveryPickUpText = () =>
    `Your order is ready for pick up, as that's your delivery method. Thank you!`

  const biometricPassedText = `Cart Updates: Your order  - ${mapOrderTypeToTitle(
    order.title,
  )} has passed biometric bypass and can be processed. Please go to www.carpaddy.com/cart to make payments for the order. Thank you!`
  const biometricPassedPushNotification = {
    deviceid: order?.deviceid,
    title: "Driver's Licence Update",
    body: "Your driver's licence has passed biometric bypass. Please open your CarPaddy app to make payments for the order",
  }

  const biometricFailedText = `Cart Updates: Sorry, your order  - ${mapOrderTypeToTitle(
    order.title,
  )} cannot be completely processed online because it cannot bypass biometrics. You can continue with your order only if you can visit FRSC office Ojodu, Lagos. If not, you can visit any FRSC office close to you by yourself to renew your licence!`
  const biometricFailedPushNotification = {
    deviceid: order?.deviceid,
    title: "Driver's Licence Update",
    body: "Sorry, your licence cannot bypass biometrics. Continue your order only if you can visit FRSC Office Ojodu, Lagos",
  }

  const processStartedText = `Order Updates: Hello ${
    order.name
  }, the processing of your order  - ${mapOrderTypeToTitle(order.title)} ${
    order?.label
  } has started and you can track your orders at https://app.carpaddy.com/orders. Thank you!`

  const processCompletedText = `Order Updates: Your order  - ${mapOrderTypeToTitle(
    order.title,
  )} ${
    order.label
  } has been processed and now awaiting delivery. Track your order at https://app.carpaddy.com/orders. Thank you!`

  const orderCompletedText = `Order Completed: Please 🙏🙏, give us a good review on our google, click https://g.page/carpaddy-ng/review?gm`

  const referUsText = `Earn N100,000+ on CarPaddy: Just give your referral code *${order.referralCode}* to your friends to sign-up on CarPaddy.com or simply share this link http://app.carpaddy.com/signup?ref="${order.referralCode}" on your pages. Thank you!`

  const onlineText =
    order?.deliveryMode === "pickup"
      ? deliveryPickUpText()
      : deliveryOnlineText()

  const simonText = deliveryText("Simon", "+2348161142905")
  const gbadeText = deliveryText("Gbade", "+2348038373022")
  const zoomText = deliveryCourierText("Zoom", "+2349080300062")
  const porterText = deliveryCourierText("Porter Express", "+2349066564406")
  const abText = `Your order has been sent: Expect a call from our delivery partner (Ab Logisitics - +2347046145125) between 9am-7pm in 1 week. The OTP for the delivery is - ${otp}. Thank you for choosing CarPaddy.com`

  const biometricPassedTextUrl = generateWhatsappUrl(
    biometricPassedText,
    order.phone,
  )

  const biometricFailedTextUrl = generateWhatsappUrl(
    biometricFailedText,
    order.phone,
  )
  const biometricFailedEmail = generateEmailUpdate(
    biometricFailedText,
    order.email,
    "Licence cannot bypass biometrics",
  )

  const processStartedTextUrl = generateWhatsappUrl(
    processStartedText,
    order.phone,
  )
  const processCompletedTextUrl = generateWhatsappUrl(
    processCompletedText,
    order.phone,
  )
  const orderCompletedTextUrl = generateWhatsappUrl(
    orderCompletedText,
    order.phone,
  )
  const referUsTextUrl = generateWhatsappUrl(referUsText, order.phone)
  const simonTextUrl = generateWhatsappUrl(simonText, order.phone)
  const onlineTextUrl = generateWhatsappUrl(onlineText, order.phone)
  const gbadeTextUrl = generateWhatsappUrl(gbadeText, order.phone)
  const zoomTextUrl = generateWhatsappUrl(zoomText, order.phone)
  const porterTextUrl = generateWhatsappUrl(porterText, order.phone)
  const abTextUrl = generateWhatsappUrl(abText, order.phone)
  const customTextUrl = generateWhatsappUrl("Hello, ", order.phone)

  return {
    biometricPassedTextUrl,
    biometricFailedTextUrl,
    biometricFailedEmail,
    biometricPassedPushNotification,
    biometricFailedPushNotification,
    processStartedTextUrl,
    processCompletedTextUrl,
    orderCompletedTextUrl,
    referUsTextUrl,
    simonTextUrl,
    gbadeTextUrl,
    zoomTextUrl,
    porterTextUrl,
    abTextUrl,
    customTextUrl,
    onlineTextUrl,
  }
}

// const classify = useCallback(
//   (
//     classifyOrders: any,
//     mergeArrays: any,
//     data: any,
//     profiles: any,
//     vehicles: any
//   ) =>
//     Object.entries(
//       classifyOrders(
//         mergeArrays(
//           vehicles,
//           mergeArrays(profiles, data, "uid", "profile"),
//           "vid",
//           "vehicle"
//         )
//       )
//     ),
//   [data, vehicles, profiles]
// )
